import React from "react";
import classnames from "classnames";
import { Link } from "react-router";
import Select from "react-select";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  Label,
  FormGroup,
  InputGroup,
  InputGroupText,
  Input,
  Row,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import ImageUploadContainer, {
  type PickedImage,
} from "../User/adding-user/ImageUploadContainer";
import type { SelectOption } from "@/utils/selectOptions";

// UpdateClientContainer.tsx loads `client` and `products` from the server
// record (the slice types both as `any`), so every field the view guards with
// `&&` / `|| ""` is optional here. `clientImg` is the container's `PickedImage`
// state, seeded with {} until an image is picked (SCRUM-2129).
export type UpdateClientViewProps = {
  client: {
    name?: string;
    about?: string;
    contacts?: { email?: string; number?: string; website?: string };
    social_links?: { facebook?: string; linkedin?: string; twitter?: string };
    adresse?: string;
    country_code?: string;
    legal_name?: string;
    clientImg?: string;
  };
  products: {
    _id: string;
    productImg?: string;
    title?: string;
    country_code?: string;
    active?: boolean;
  }[];
  updatedCountries: readonly SelectOption[];
  validation: { name: boolean; country_code: boolean };
  permission: { edit?: boolean; publish?: boolean };
  loading: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onChangeCountryCode: (option: SelectOption | null) => void;
  onClickUpdate: React.MouseEventHandler<HTMLButtonElement>;
  ImageUploadRecievedHandler: (img: PickedImage) => void;
  clientImg: PickedImage;
};

const UpdateClientView = ({
  client,
  products,
  updatedCountries,
  validation,
  permission,
  loading,
  onChange,
  onChangeCountryCode,
  onClickUpdate,
  ImageUploadRecievedHandler,
  clientImg,
}: UpdateClientViewProps) => {
  return (
    <>
      {/* NOTIFICATION DIV */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <SimpleHeader name="Update Client" parentName="Clients" />

      <Container className="mt--6" fluid>
        <Row>
          <Col className="order-xl-1" xl="8">
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">Update Client</h3>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <h6 className="heading-small text-muted mb-4">
                  General information
                </h6>

                {/* Name */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Name
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Name"
                        name="name"
                        type="text"
                        value={client.name || ""}
                        onChange={onChange}
                        invalid={!validation.name}
                      />
                    </InputGroup>
                    {!validation.name ? (
                      <InputGroup className="invalid-feedback">
                        Required State.
                      </InputGroup>
                    ) : (
                      ""
                    )}
                  </Col>
                </FormGroup>

                {/* Legal Name */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Legal Name
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Legal Name"
                        name="legal_name"
                        type="text"
                        value={client.legal_name || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* About */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    About
                  </Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="About the company .."
                        name="about"
                        type="textarea"
                        value={client.about || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Adresse */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Adresse
                  </Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Adresse"
                        name="adresse"
                        type="text"
                        value={client.adresse || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Country */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Country
                  </Label>
                  <Col md="9">
                    <Select
                      value={updatedCountries.filter(
                        (opt) => opt.value === client.country_code
                      )}
                      name="country_code"
                      options={updatedCountries}
                      onChange={onChangeCountryCode}
                      className={!validation.country_code ? "is-invalid" : ""}
                    />
                    {!validation.country_code ? (
                      <InputGroup className="invalid-feedback">
                        Required Country.
                      </InputGroup>
                    ) : (
                      ""
                    )}
                  </Col>
                </FormGroup>

                <hr className="my-4" />
                <h6 className="heading-small text-muted mb-4">Contact</h6>

                {/* Website */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Website
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="ni ni-world" />
                      </InputGroupText>
                      <Input
                        placeholder="https://www.example.com/.."
                        name="website"
                        type="text"
                        value={(client.contacts && client.contacts.website) || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                {/* Email */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Email
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="ni ni-email-83" />
                      </InputGroupText>
                      <Input
                        placeholder="example@example.com"
                        name="email"
                        type="text"
                        value={(client.contacts && client.contacts.email) || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                {/* Number (tel pour éviter les flèches et autoriser +/espaces) */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Number
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="fa fa-phone" />
                      </InputGroupText>
                      <Input
                        placeholder="+ xx xx xx xx "
                        name="number"
                        type="tel"
                        value={(client.contacts && client.contacts.number) || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                <hr className="my-4" />
                <h6 className="heading-small text-muted mb-4">
                  Social Media
                </h6>

                {/* Linkedin */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Linkedin
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="fab fa-linkedin" />
                      </InputGroupText>
                      <Input
                        placeholder="https://www.linkedin.com/company/.."
                        name="linkedin"
                        type="text"
                        value={
                          (client.social_links && client.social_links.linkedin) ||
                          ""
                        }
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                {/* Facebook */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Facebook
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="fab fa-facebook" />
                      </InputGroupText>
                      <Input
                        placeholder="https://www.facebook.com/.."
                        name="facebook"
                        type="text"
                        value={
                          (client.social_links && client.social_links.facebook) ||
                          ""
                        }
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                {/* Twitter */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Twitter
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="fab fa-twitter" />
                      </InputGroupText>
                      <Input
                        placeholder="https://twitter.com.."
                        name="twitter"
                        type="text"
                        value={
                          (client.social_links && client.social_links.twitter) ||
                          ""
                        }
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                <hr className="my-4" />
                {(permission?.edit || permission?.publish) ? (
                  <Button color="primary" onClick={onClickUpdate} disabled={loading}>
                    {loading ? "Updating..." : "Update"}
                  </Button>
                ) : null}

                <Button
                  to={"/admin/client/all"}
                  tag={Link}
                  className="btn btn-default "
                >
                  All Clients
                </Button>
              </CardBody>
            </Card>
          </Col>

          {/* Colonne droite : Upload + Produits */}
          <Col className="order-xl-2" xl="4">
            <Card className="card-pricing border-0 text-center mb-4">
              <CardHeader className="bg-gradient-success">
                <h4 className="text-uppercase ls-0 text-white py-2 mb-0">
                  Update Logo
                </h4>
              </CardHeader>
              <CardBody className="px-lg-2">
                <ImageUploadContainer
                  ImageUpload={ImageUploadRecievedHandler}
                  image={
                    clientImg?.secure_url
                      ? clientImg.secure_url
                      : client.clientImg
                  }
                />
              </CardBody>
            </Card>

            {products?.map((value) => (
              <React.Fragment key={value._id}>
                <Card>
                  <CardBody>
                    <Row className="align-items-center">
                      <Col className="col-auto" md="2">
                        <span className="avatar avatar-xl rounded-circle">
                          <img alt="..." src={value.productImg || ""} />
                        </span>
                      </Col>
                      <Col className="col ml-5" md="5">
                        <h4 className="mb-0">
                          <a href="#!" onClick={(e) => e.preventDefault()}>
                            {value.title}
                          </a>
                        </h4>
                        <p className="text-sm text-muted mb-0">
                          {value.country_code}
                        </p>
                        {value.active ? (
                          <>
                            <span className="text-success mr-1">●</span>
                            <small>Active</small>
                          </>
                        ) : (
                          <>
                            <span className="text-danger mr-1">●</span>
                            <small>Active</small>
                          </>
                        )}
                      </Col>
                      <Col className="float-right" md="2">
                        <Button
                          tag={Link}
                          to={`/admin/update-product/${value._id}`}
                          color="primary"
                          size="sm"
                          type="button"
                        >
                          View
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </React.Fragment>
            ))}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default UpdateClientView;
